import { useMethod } from 'react-method-elements';
import { useNavigate, generatePath } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import { useEffect } from 'react';

// Our Hooks
import useMutateGetMethodfiToken from 'hooks/methodfi/useMutateGetMethodfiToken';

// Our Query Keys
import { ERROR_MESSAGE_DATA, METHODFI_ENTITY_ID } from 'shared/query-keys';

// Our Hooks
import useGetProfile from 'hooks/client/useGetProfile';
import useGetAssetProvider from 'hooks/assetProvider/useGetAssetProvider';
import useMutateSendMethodfiUpdateLog from 'hooks/log/useMutateSendMethodfiUpdateLog';

// Our Routes
import {
	METHODFI_UNABLE_TO_VERIFY_ROUTE,
	LOAD_LIABILITIES_ROUTE as PROCESS_METHODFI_LIABILITIES_ROUTE
} from 'routes';

// Our Constants
import {
	METHODFI_OPEN,
	METHODFI_ERROR,
	METHODFI_SUCCESS,
	METHODFI_USER_EXITED
} from 'shared/constants';

// Constant that declares the Environment Method will work in
const METHODFI_ENVIRONMENT = process.env.REACT_APP_METHODFI_ENV;

function useMethodfiConnect(
	userDetails,
	beginMethodTokenRequest,
	setBeginMethodTokenRequest,
	setAccountConnectionSuccess
) {
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const getMethodfiToken = useMutateGetMethodfiToken();
	const sendMethodfiUpdateLog = useMutateSendMethodfiUpdateLog();

	const { data: clientId } = useGetProfile(
		(data) => data?.id,
		beginMethodTokenRequest
	);

	const { data } = useGetAssetProvider();

	const method = useMethod({
		env: METHODFI_ENVIRONMENT, // (dev / sandbox / production)
		onSuccess: () => {
			// This Success call is fired After security questions are answered OR is bypassed via PII auth.
			const entityId = queryClient.getQueryData(METHODFI_ENTITY_ID);

			const { data: advisorProfile } = data;

			setAccountConnectionSuccess(true);

			sendMethodfiUpdateLog.mutate({
				advisorId: advisorProfile?.id,
				clientId,
				methodfiUpdates: {
					status: METHODFI_SUCCESS,
					error: ''
				}
			});

			method.close();

			const dynamic_process_path = generatePath(
				`${PROCESS_METHODFI_LIABILITIES_ROUTE}/:accountHolderId`,
				{ accountHolderId: entityId }
			);

			navigate(dynamic_process_path);
		},
		onError: async ({ sub_type: subType }) => {
			const { data: advisorProfile } = data;

			await sendMethodfiUpdateLog.mutate({
				advisorId: advisorProfile?.id,
				clientId,
				methodfiUpdates: {
					status: METHODFI_ERROR,
					error: subType
				}
			});

			if (subType === 'ENTITY_AUTH_SESSION_LOCKED') {
				queryClient.setQueryData(ERROR_MESSAGE_DATA, [
					'Your account is temporarily locked. Please try again later.',
					'Contact support at contact@sorafinance.com if you continue to experience issues.'
				]);
				navigate(METHODFI_UNABLE_TO_VERIFY_ROUTE);
			}
			method.close();
		},
		onExit: () => {
			const { data: advisorProfile } = data;

			sendMethodfiUpdateLog.mutate({
				advisorId: advisorProfile?.id,
				clientId,
				methodfiUpdates: {
					status: METHODFI_USER_EXITED,
					error: ''
				}
			});
			setBeginMethodTokenRequest(false);
		},
		onOpen: (e) => {
			const { data: advisorProfile } = data;

			sendMethodfiUpdateLog.mutate({
				advisorId: advisorProfile?.id,
				clientId,
				methodfiUpdates: {
					status: METHODFI_OPEN,
					error: ''
				}
			});
		}
	});

	// Treat this effect as an event listener
	// this event listener is listening to beginMethodToken Request
	// if that value changes it will run the function
	// if the value changes and its TRUE it will fire the api call.
	useEffect(() => {
		if (beginMethodTokenRequest) {
			// if this is true it means we want to begin the request for the token.
			const { dob, firstName, lastName, phoneNumber, ssn } = userDetails;

			let methodfiAcceptedDate = dob;

			if (typeof dob !== 'string') {
				const parsedDob = dob.toLocaleDateString('en-US', {
					year: 'numeric',
					day: '2-digit',
					month: '2-digit'
				});

				const splitDate = parsedDob.split('/');

				const year = splitDate[2];
				const day = splitDate[1];
				const month = splitDate[0];

				methodfiAcceptedDate = `${year}-${month}-${day}`;
			}

			const cleanedPhoneNumber =
				phoneNumber && phoneNumber.replaceAll('-', '');

			const userDetailsSentToMFI = {
				first_name: firstName,
				last_name: lastName,
				phone:
					METHODFI_ENVIRONMENT === 'dev'
						? '+15121231111'
						: cleanedPhoneNumber,
				dob: methodfiAcceptedDate
			};

			const hasSSNAndNotEmpty = ssn && ssn.length > 0;

			if (hasSSNAndNotEmpty)
				userDetailsSentToMFI.ssn = ssn.replaceAll('-', '');

			getMethodfiToken.mutate(userDetailsSentToMFI, {
				onSuccess: (tokenData) => {
					if (
						tokenData?.message ===
						"User's profile not found in MethodFI"
					) {
						navigate(METHODFI_UNABLE_TO_VERIFY_ROUTE);
					}
					const { elementsId, entityId } = tokenData;
					queryClient.setQueryData(METHODFI_ENTITY_ID, entityId);
					method.open(elementsId);
				},
				onError: () => {
					setBeginMethodTokenRequest(false);
				}
			});
		}
	}, [beginMethodTokenRequest]); // this gets triggered when beginMethodTokenRequest changes
}

export default useMethodfiConnect;
